import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Components
import CookieConsent from "@/components/CookieConsent";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import TrainingMode from "@/pages/TrainingMode";
import ExamMode from "@/pages/ExamMode";
import CustomSession from "@/pages/CustomSession";
import Results from "@/pages/Results";
import History from "@/pages/History";
import AdminPanel from "@/pages/AdminPanel";
import Store from "@/pages/Store";
import ExamSimulation from "@/pages/ExamSimulation";
import AuthCallback from "@/pages/AuthCallback";
import CookiePolicy from "@/pages/CookiePolicy";
import Disclaimer from "@/pages/Disclaimer";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import Upgrade from "@/pages/Upgrade";
import WordSearch from "@/pages/WordSearch";
import PaymentSuccess from "@/pages/PaymentSuccess";
import GlossaryPage from "@/pages/GlossaryPage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

const API = `${window.location.origin || ''}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const checkDone = useRef(false);

  useEffect(() => {
    // Prevent duplicate checks
    if (checkDone.current) return;
    
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsChecking(false);
      checkDone.current = true;
      return;
    }

    const verify = async () => {
      checkDone.current = true;
      const isAuthed = await checkAuth();
      setIsChecking(false);
      if (!isAuthed) {
        navigate('/login', { replace: true });
      }
    };
    verify();
  }, []); // Empty deps - only run once on mount

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/training" element={
        <ProtectedRoute>
          <TrainingMode />
        </ProtectedRoute>
      } />
      
      <Route path="/exam" element={
        <ProtectedRoute>
          <ExamMode />
        </ProtectedRoute>
      } />
      
      <Route path="/custom" element={
        <ProtectedRoute>
          <CustomSession />
        </ProtectedRoute>
      } />
      
      <Route path="/results/:attemptId" element={
        <ProtectedRoute>
          <Results />
        </ProtectedRoute>
      } />
      
      <Route path="/history" element={
        <ProtectedRoute>
          <History />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminPanel />
        </ProtectedRoute>
      } />

      <Route path="/store" element={
        <ProtectedRoute>
          <Store />
        </ProtectedRoute>
      } />

      <Route path="/upgrade" element={
        <ProtectedRoute>
          <Upgrade />
        </ProtectedRoute>
      } />

      <Route path="/word-search" element={
        <ProtectedRoute>
          <WordSearch />
        </ProtectedRoute>
      } />

      <Route path="/glossary" element={
        <ProtectedRoute>
          <GlossaryPage />
        </ProtectedRoute>
      } />

      {/* Redirect old crossword URL */}
      <Route path="/crossword" element={<Navigate to="/word-search" replace />} />

      {/* Payment Success Page */}
      <Route path="/payment-success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />

      <Route path="/exam-simulation" element={
        <ProtectedRoute>
          <ExamSimulation />
        </ProtectedRoute>
      } />

      {/* Legal Pages */}
      <Route path="/terms" element={<TermsOfUse />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App min-h-screen">
          <BrowserRouter>
            <AppRouter />
            <CookieConsent />
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export { API };
export default App;
